/**
 * TOTP verification action creator (be-602 / be-603).
 *
 * Second step of a TOTP-gated login. `performLogin` returns
 * `{kind: "totp-required", totpSessionToken, ttl}` for an authenticator-enrolled
 * account; this action completes the challenge.
 *
 * Posts `{totpSessionToken, code, audience}` to `/api/v1/auth/totp/verify` (or,
 * for `audience: "platform"`, `{totpSessionToken, code}` to
 * `/api/v1/_platform/auth/totp/verify`) with
 * BOTH the `Authorization: Bearer <totpSessionToken>` header AND the same token
 * in the body (the backend contract requires both). `code` is a 6-digit TOTP
 * code OR a recovery code (6–16 chars). The backend mints the access token HERE
 * — never in the login step (same D-11 security boundary as OTP).
 *
 * On success: returns `{snapshot}` identical in shape to login's Response A, so
 * callers complete login uniformly.
 *
 * On failure: throws an ApiError-shaped object with a stable `errorCode`:
 *   - TOTP_INVALID_CODE / TOTP_RECOVERY_CODE_INVALID → wrong / expired code
 *     (stay on the challenge step, show an inline error).
 *   - TOTP_SESSION_INVALID / TOTP_SESSION_EXPIRED → the 5-minute session JWT is
 *     gone; the form bounces back to the credentials step.
 *   - TOTP_SETUP_REQUIRED (409) → the account is not actually enrolled.
 *
 * GOTCHA (be-603): a garbage / undecodable session token yields a BODYLESS 401
 * (no RFC-7807 envelope). We can't read an `errorCode` off it, so any 401 whose
 * body carries no `errorCode` is treated as `TOTP_SESSION_INVALID` — i.e.
 * "session expired, restart sign-in" — via the status fallback below.
 */
import { commitVerifiedSession } from "./commitVerifiedSession.js";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import { parseRetryAfterSeconds } from "../refresh/performRefresh.js";
import type { AuthSnapshot, Audience } from "../types.js";

export interface VerifyTotpArgs {
  totpSessionToken: string;
  /** 6-digit authenticator code OR a recovery code (6–16 chars). */
  code: string;
  apiBaseUrl: string;
  /**
   * Target audience (admin | mobile) — REQUIRED by the backend TotpVerifyRequest,
   * and MUST match the audience used at login. useAuth injects it from app
   * context (same as login / OTP).
   */
  audience: Audience;
  /** What the user typed at the password step — names them in the shell. */
  emailHint?: string | undefined;
}

// Mirrors the generated `LoginTokensResponse` — the verify success shape is
// identical to a normal login success. Only `accessToken` is load-bearing here;
// the rest is honest-optional so a tokens-only backend response still works.
interface TotpSuccessBody {
  type?: string;
  accessToken: string;
  refreshToken?: string | null;
  user?: {
    id: string;
    email?: string | null;
    displayName: string;
    roles?: string[];
    username?: string | null;
  };
  mustChangePassword?: boolean;
}

export async function verifyTotp({
  totpSessionToken,
  code,
  apiBaseUrl,
  audience,
  emailHint,
}: VerifyTotpArgs): Promise<{ snapshot: AuthSnapshot }> {
  // Platform staff verify on their own endpoint, which takes no audience.
  const isPlatform = audience === "platform";
  const res = await fetch(
    apiBaseUrl + (isPlatform ? "/api/v1/_platform/auth/totp/verify" : "/api/v1/auth/totp/verify"),
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAcceptLanguageHeader(),
        // Contract requires the session token in the header AND the body.
        Authorization: `Bearer ${totpSessionToken}`,
      },
      body: JSON.stringify(
        isPlatform ? { totpSessionToken, code } : { totpSessionToken, code, audience },
      ),
    },
  );
  // A bodyless / unparseable response falls back to `{}` (no errorCode) — the
  // status fallback below then classifies a 401 as a dead session.
  const body = (await res.json().catch(() => ({}))) as
    | TotpSuccessBody
    | { errorCode?: string; title?: string };

  if (!res.ok) {
    const e = body as { errorCode?: string; title?: string };
    // 401 without a stable errorCode = the bodyless "undecodable session"
    // case → treat as a dead session so the form restarts sign-in. 409 = the
    // account isn't enrolled. 429 (bodyless) = too many wrong codes for this
    // person; Retry-After says how long. Anything else defaults to a bad code.
    const fallbackCode =
      res.status === 409
        ? "TOTP_SETUP_REQUIRED"
        : res.status === 401
          ? "TOTP_SESSION_INVALID"
          : res.status === 429
            ? "AUTH_RATE_LIMITED"
            : "TOTP_INVALID_CODE";
    throw Object.assign(new Error(e.title ?? "TOTP verification failed"), {
      // The RFC 7807 title as a real property — consumers branch on it
      // (message alone is not introspectable as "the server copy").
      title: e.title,
      status: res.status,
      errorCode: e.errorCode ?? fallbackCode,
      correlationId: res.headers.get("x-correlation-id"),
      retryAfterSeconds: parseRetryAfterSeconds(res),
      isApiError: true,
    });
  }

  const success = body as TotpSuccessBody;
  // Validate the token at the shared security boundary BEFORE trusting it — a
  // garbage / expired / wrong-audience token must never mint a session even on
  // a 2xx. commitVerifiedSession throws AUTH_UNEXPECTED_RESPONSE and stores
  // nothing on any failure.
  const snapshot = await commitVerifiedSession({
    accessToken: success.accessToken,
    expectedAudience: audience,
    apiBaseUrl,
    bodyUser: success.user,
    emailHint,
    // Platform admins keep role-based gating (see platformLogin.ts).
    hydratePermissions: !isPlatform,
    mustChangePassword: success.mustChangePassword === true,
    correlationId: res.headers.get("x-correlation-id"),
  });
  return { snapshot };
}
